// src/components/Warehouses/WarehouseDetailModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { X, Package, Search, AlertTriangle, TrendingUp, TrendingDown, Minus, Tag } from 'lucide-react';

const WarehouseDetailModal = ({ isOpen, onClose, warehouseId, warehouseName }) => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total_items: 0,
    total_skus: 0,
    low_stock_items: 0,
    pre_orderable_items: 0
  });

  // Fetch warehouse inventory - wrapped in useCallback to prevent infinite re-renders
  const fetchWarehouseInventory = useCallback(async () => {
    if (!warehouseId) return;

    try {
      setLoading(true);
      setError('');
      console.log(`🔄 Loading inventory for warehouse ${warehouseId}...`);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/dashboard/warehouse/${warehouseId}/inventory`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch warehouse ${warehouseId} inventory`);
      }

      const data = await response.json();
      console.log('Warehouse inventory modal data:', data);

      const warehouseData = data.warehouse_inventory;
      if (warehouseData && warehouseData.success) {
        // Filter items with stock > 0
        const inventoryItems = warehouseData.inventory.filter(item =>
          (item.total_stock_level || item.level || 0) > 0
        );
        setInventory(inventoryItems);

        // Calculate statistics
        const totalItems = inventoryItems.reduce((sum, item) =>
          sum + (item.total_stock_level || item.level || 0), 0
        );
        const lowStockItems = inventoryItems.filter(item =>
          item.is_low_stock || (item.level <= item.low_stock_level)
        ).length;
        const preOrderableItems = inventoryItems.filter(item => item.pre_orderable).length;

        setStats({
          total_items: totalItems,
          total_skus: inventoryItems.length,
          low_stock_items: lowStockItems,
          pre_orderable_items: preOrderableItems
        });

        console.log(`✅ Loaded ${inventoryItems.length} items with stock > 0`);
      } else {
        throw new Error(warehouseData?.error?.message || 'Failed to load warehouse data');
      }

    } catch (error) {
      console.error('❌ Error fetching warehouse inventory:', error);
      setError('Failed to load inventory: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [warehouseId]); // Only recreate function when warehouseId changes

  // Load data when modal opens or warehouseId changes
  useEffect(() => {
    if (isOpen && warehouseId) {
      fetchWarehouseInventory();
    }
  }, [isOpen, warehouseId, fetchWarehouseInventory]);

  // Reset data when modal closes
  useEffect(() => {
    if (!isOpen) {
      setInventory([]);
      setStats({
        total_items: 0,
        total_skus: 0,
        low_stock_items: 0,
        pre_orderable_items: 0
      });
      setSearchTerm('');
      setError('');
    }
  }, [isOpen]);

  // Filter inventory by search
  const filteredInventory = inventory.filter(item =>
    item.sku && item.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get stock status badge
  const getStockStatusBadge = (item) => {
    const isLowStock = item.is_low_stock || (item.level <= item.low_stock_level);
    const isOutOfStock = item.is_out_of_stock || (item.level === 0);

    if (isOutOfStock) {
      return { text: 'Out of Stock', color: '#dc2626', bg: '#fef2f2', icon: <Minus size={10} /> };
    } else if (isLowStock) {
      return { text: 'Low Stock', color: '#d97706', bg: '#fef3c7', icon: <TrendingDown size={10} /> };
    } else {
      return { text: 'In Stock', color: '#16a34a', bg: '#f0fdf4', icon: <TrendingUp size={10} /> };
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        style={{maxWidth: '90vw', maxHeight: '90vh', width: '1000px'}}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="modal-header">
          <h2 className="modal-title">
            {warehouseName || `Warehouse ${warehouseId}`} - Inventory Details
          </h2>
          <button onClick={onClose} className="modal-close">
            <X size={20} />
          </button>
        </div>

        <div style={{padding: '0 1.5rem 1.5rem 1.5rem'}}>
          {/* Stats Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{
              background: '#f8fafc',
              padding: '1rem',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem'}}>
                <Package size={16} color="#3b82f6" />
                <span style={{fontWeight: '500', color: '#374151'}}>Total Items</span>
              </div>
              <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937'}}>
                {stats.total_items.toLocaleString()}
              </div>
              <div style={{fontSize: '0.75rem', color: '#6b7280'}}>
                {stats.total_skus} unique SKUs
              </div>
            </div>

            <div style={{
              background: '#f8fafc',
              padding: '1rem',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem'}}>
                <AlertTriangle size={16} color="#d97706" />
                <span style={{fontWeight: '500', color: '#374151'}}>Low Stock</span>
              </div>
              <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#d97706'}}>
                {stats.low_stock_items}
              </div>
              <div style={{fontSize: '0.75rem', color: '#6b7280'}}>
                Need attention
              </div>
            </div>

            <div style={{
              background: '#f8fafc',
              padding: '1rem',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem'}}>
                <Tag size={16} color="#16a34a" />
                <span style={{fontWeight: '500', color: '#374151'}}>Pre-Order</span>
              </div>
              <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#16a34a'}}>
                {stats.pre_orderable_items}
              </div>
              <div style={{fontSize: '0.75rem', color: '#6b7280'}}>
                Available for pre-order
              </div>
            </div>
          </div>

          {/* Search */}
          <div style={{position: 'relative', marginBottom: '1rem'}}>
            <Search size={16} style={{
              position: 'absolute',
              left: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af'
            }} />
            <input
              type="text"
              placeholder="Search by SKU..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '0.875rem'
              }}
            />
          </div>

          {/* Content */}
          {loading ? (
            <div style={{textAlign: 'center', padding: '2rem'}}>
              <div className="loading-spinner" style={{margin: '0 auto'}}></div>
              <p style={{marginTop: '1rem', color: '#6b7280'}}>Loading inventory...</p>
            </div>
          ) : error ? (
            <div className="error-message">
              <AlertTriangle size={20} />
              <div>
                <h4>Error Loading Inventory</h4>
                <p className="error-text">{error}</p>
                <button
                  onClick={fetchWarehouseInventory}
                  className="btn-primary"
                  style={{marginTop: '0.5rem', width: 'auto', padding: '0.5rem 1rem'}}
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : (
            <div style={{maxHeight: '400px', overflowY: 'auto'}}>
              <table style={{width: '100%', borderCollapse: 'collapse'}}>
                <thead style={{position: 'sticky', top: 0, background: 'white', zIndex: 1}}>
                  <tr style={{borderBottom: '2px solid #e5e7eb'}}>
                    <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>SKU</th>
                    <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Stock</th>
                    <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Available</th>
                    <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Status</th>
                    <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Features</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventory.slice(0, 50).map((item, index) => {
                    const statusBadge = getStockStatusBadge(item);
                    const stockLevel = item.total_stock_level || item.level || 0;
                    const availableLevel = item.available_level || item.level || 0;

                    return (
                      <tr key={item.product_id || index} style={{borderBottom: '1px solid #f3f4f6'}}>
                        <td style={{padding: '0.75rem'}}>
                          <div style={{fontWeight: '500', color: '#1f2937', fontFamily: 'monospace', fontSize: '0.875rem'}}>
                            {item.sku}
                          </div>
                          {item.product_id && (
                            <div style={{fontSize: '0.75rem', color: '#6b7280'}}>
                              ID: {item.product_id}
                            </div>
                          )}
                        </td>

                        <td style={{padding: '0.75rem'}}>
                          <span style={{fontWeight: '600', color: '#1f2937'}}>
                            {stockLevel.toLocaleString()}
                          </span>
                        </td>

                        <td style={{padding: '0.75rem'}}>
                          <span style={{fontWeight: '500', color: '#374151'}}>
                            {availableLevel.toLocaleString()}
                          </span>
                        </td>

                        <td style={{padding: '0.75rem'}}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.125rem 0.375rem',
                            borderRadius: '3px',
                            fontSize: '0.625rem',
                            fontWeight: '500',
                            backgroundColor: statusBadge.bg,
                            color: statusBadge.color
                          }}>
                            {statusBadge.icon}
                            {statusBadge.text}
                          </span>
                        </td>

                        <td style={{padding: '0.75rem'}}>
                          <div style={{display: 'flex', gap: '0.25rem'}}>
                            {item.pre_orderable && (
                              <span style={{
                                fontSize: '0.625rem',
                                padding: '0.125rem 0.25rem',
                                backgroundColor: '#e0f2fe',
                                color: '#0369a1',
                                borderRadius: '2px',
                                fontWeight: '500'
                              }}>
                                PreOrder
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {filteredInventory.length === 0 && (
                <div style={{textAlign: 'center', padding: '2rem', color: '#6b7280'}}>
                  <Package size={48} style={{opacity: 0.5, margin: '0 auto 0.75rem'}} />
                  <p>No inventory items found</p>
                </div>
              )}

              {filteredInventory.length > 50 && (
                <div style={{textAlign: 'center', padding: '1rem', color: '#6b7280', fontSize: '0.875rem'}}>
                  Showing first 50 items of {filteredInventory.length} total
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WarehouseDetailModal;